import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const EmpresaPage = () => {
  useEffect(() => {
    document.title = 'Empresa - Instalación de Pladur Barcelona | PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conoce PLADUREX, especialistas en instalación de pladur en Barcelona. Profesionales con experiencia en tabiques, trasdosados y falsos techos. Calidad garantizada.');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://barcelona.pladures.es';

    // Add WebPage Schema
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Empresa - Instalación de Pladur Barcelona",
      "description": "Conoce PLADUREX, especialistas en instalación de pladur en Barcelona. Profesionales con experiencia en tabiques, trasdosados y falsos techos.",
      "url": `${siteUrl}/empresa`,
      "inLanguage": "es-ES",
      "isPartOf": {
        "@type": "WebSite",
        "name": "PLADUREX Barcelona",
        "url": siteUrl
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${siteUrl}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Empresa",
            "item": `${siteUrl}/empresa`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(webPageSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 bg-gradient-to-br from-primary-dark to-primary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://public.readdy.ai/ai/img_res/b112b3b5277faade50e189ab27afefc0.jpg"
              alt="Equipo PLADUREX"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-white font-extrabold text-3xl sm:text-5xl md:text-6xl mb-6"
            >
              Especialistas en instalación de pladur en Barcelona
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-white/90 text-xl leading-relaxed max-w-3xl mx-auto"
            >
              Somos profesionales dedicados a ofrecer soluciones de pladur con la máxima calidad, rapidez y acabado impecable para viviendas, oficinas y locales en toda Barcelona.
            </motion.p>
          </div>
        </section>

        {/* Quiénes Somos */}
        <section className="py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-primary-dark text-4xl md:text-5xl font-serif font-bold mb-6">
                  Quiénes somos
                </h2>
                <p className="text-gray-600 text-base leading-relaxed mb-4">
                  <strong>PLADUREX</strong> es una empresa especializada en la <Link to="/servicios" className="text-primary hover:underline font-semibold">instalación de pladur en Barcelona</Link> y área metropolitana. Desde nuestra base en Sant Andreu, atendemos proyectos en toda la ciudad, incluyendo Eixample, Sants, Les Corts, Gràcia, Sant Martí y Ciutat Vella.
                </p>
                <p className="text-gray-600 text-base leading-relaxed mb-4">
                  Nuestro equipo cuenta con amplia experiencia en la ejecución de <Link to="/servicios" className="text-primary hover:underline">tabiques, trasdosados, falsos techos</Link> y sistemas de <Link to="/servicios" className="text-primary hover:underline">aislamiento acústico y térmico</Link>. Trabajamos tanto en reformas integrales como en proyectos específicos, siempre con el mismo compromiso de calidad.
                </p>
                <p className="text-gray-600 text-base leading-relaxed">
                  Nos caracterizamos por nuestra profesionalidad, puntualidad y respeto hacia el espacio de nuestros clientes. Entendemos que cada proyecto es único y nos adaptamos a las necesidades específicas de cada cliente. ¿Tienes dudas? Consulta nuestras <Link to="/faq" className="text-primary hover:underline font-semibold">preguntas frecuentes</Link>.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20worker%20measuring%20white%20drywall%20installation%20with%20tools%20bright%20interior&width=800&height=900&seq=empresa-img-001&orientation=portrait"
                  alt="Profesional instalando pladur"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Cómo Trabajamos */}
        <section className="py-24 bg-bg-light">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-primary-dark text-4xl md:text-5xl font-serif font-bold mb-6">
                Cómo trabajamos
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Nuestro método de trabajo garantiza resultados profesionales y una experiencia sin complicaciones para nuestros clientes.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-sm"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-map-pin-user-line text-primary text-3xl"></i>
                </div>
                <h3 className="text-primary-dark text-2xl font-bold mb-4">
                  Visita y medición
                </h3>
                <p className="text-gray-600 text-[15px] leading-relaxed">
                  Nos desplazamos a tu ubicación en Barcelona para evaluar el espacio, tomar medidas precisas y entender tus necesidades. Esta visita es gratuita y sin compromiso.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl p-8 shadow-sm"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-file-list-3-line text-primary text-3xl"></i>
                </div>
                <h3 className="text-primary-dark text-2xl font-bold mb-4">
                  Propuesta detallada
                </h3>
                <p className="text-gray-600 text-[15px] leading-relaxed">
                  Elaboramos un presupuesto claro y detallado que incluye materiales, mano de obra, plazos de ejecución y todas las especificaciones técnicas del proyecto de pladur.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl p-8 shadow-sm"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-tools-line text-primary text-3xl"></i>
                </div>
                <h3 className="text-primary-dark text-2xl font-bold mb-4">
                  Ejecución profesional
                </h3>
                <p className="text-gray-600 text-[15px] leading-relaxed">
                  Realizamos la instalación con técnicas profesionales, materiales de calidad y respetando los plazos acordados. Mantenemos el espacio limpio y ordenado durante todo el proceso.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl p-8 shadow-sm"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-checkbox-circle-line text-primary text-3xl"></i>
                </div>
                <h3 className="text-primary-dark text-2xl font-bold mb-4">
                  Acabado y entrega
                </h3>
                <p className="text-gray-600 text-[15px] leading-relaxed">
                  Realizamos los acabados finales con máxima precisión, dejando las superficies listas para pintar o decorar. Revisamos el trabajo contigo antes de dar por finalizado el proyecto.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Nuestro Compromiso */}
        <section className="py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="order-2 lg:order-1"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20finished%20interior%20with%20perfect%20white%20drywall%20smooth%20walls%20bright%20lighting&width=800&height=900&seq=empresa-img-002&orientation=portrait"
                  alt="Acabado profesional de pladur"
                  className="w-full h-[600px] object-cover object-top rounded-2xl shadow-2xl"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="order-1 lg:order-2"
              >
                <h2 className="text-primary-dark text-4xl md:text-5xl font-serif font-bold mb-6">
                  Nuestro compromiso de calidad
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-primary-dark text-lg font-bold mb-2">Materiales de primera calidad</h3>
                      <p className="text-gray-600 text-[15px] leading-relaxed">
                        Utilizamos únicamente placas de pladur y materiales auxiliares de marcas reconocidas, garantizando durabilidad y acabados perfectos.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-primary-dark text-lg font-bold mb-2">Trabajo en espacios ocupados</h3>
                      <p className="text-gray-600 text-[15px] leading-relaxed">
                        Coordinamos horarios y mantenemos la limpieza para que puedas seguir con tu rutina mientras realizamos la instalación de pladur.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-primary-dark text-lg font-bold mb-2">Coordinación y transparencia</h3>
                      <p className="text-gray-600 text-[15px] leading-relaxed">
                        Te mantenemos informado en cada fase del proyecto. Facturamos de forma clara y cumplimos con todos los requisitos legales.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-primary-dark text-lg font-bold mb-2">Garantía de satisfacción</h3>
                      <p className="text-gray-600 text-[15px] leading-relaxed">
                        Todos nuestros trabajos incluyen garantía. Si algo no cumple tus expectativas, lo solucionamos sin coste adicional.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-primary">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-white text-4xl md:text-5xl font-bold mb-6"
            >
              ¿Tienes un proyecto en mente?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/90 text-lg mb-8"
            >
              Contacta con nosotros y te ofreceremos un presupuesto personalizado para tu <Link to="/servicios" className="text-white underline hover:text-white/80">instalación de pladur en Barcelona</Link>.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4"
            >
              <a
                href="tel:+34629501941"
                className="bg-white text-primary px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>+34 629 501 941</span>
              </a>
              <Link
                to="/contacto"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary transition-all duration-300 whitespace-nowrap"
              >
                Formulario de contacto
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default EmpresaPage;
